import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CogIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import api, { reindexSCGRules, reindexFOIARules, getFunctionConfigs, updateFunctionConfig, testModelConfig, getLLMModelsWithInfo } from '../services/api';
import {
  validateSectionConfig,
  getFieldWarnings,
  hasUnsavedChanges,
  getChangedFields
} from '../utils/validation';
import { SettingTooltip } from '../components/Tooltip';
import { getSettingTooltip } from '../utils/settingTooltips';
import ModelSwitcher from '../components/ModelSwitcher';

// ── Per-Function Model Configuration Component ──────────────────────────────
const PerFunctionModelConfig = () => {
  const [edits, setEdits] = useState({});       // { functionName: { model_id, temperature, max_tokens } }
  const [testResults, setTestResults] = useState({});  // { functionName: { status, latency, error } }
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Fetch per-function configs
  const { data: configs, isLoading: configsLoading, refetch: refetchConfigs } = useQuery({
    queryKey: ['functionModelConfigs'],
    queryFn: getFunctionConfigs,
    staleTime: 30 * 1000,
  });

  // Fetch available models with metadata (for model dropdown). NOTE: this
  // used to hit GET /models/providers, which on the canonical router
  // (model_config_endpoints.py) returns List[str] (provider names only, no
  // model entries) — the Array.isArray guard below silently produced an
  // empty dropdown. /models/available returns Dict[str, List[modelInfo]],
  // which is what the parsing logic here actually expects.
  const { data: providers, isLoading: providersLoading } = useQuery({
    queryKey: ['modelsAvailable'],
    // Wrapped (not passed directly) so react-query's queryFn context object
    // isn't accidentally forwarded as the `provider` filter argument.
    queryFn: () => getLLMModelsWithInfo(),
    staleTime: 10 * 60 * 1000,
  });

  // Build flat model list from providers
  const allModels = React.useMemo(() => {
    if (!providers) return [];
    const models = [];
    Object.entries(providers).forEach(([providerName, providerModels]) => {
      if (!Array.isArray(providerModels)) return;
      providerModels.forEach(m => {
        const modelId = m.model_id || m.id || m;
        const displayName = m.display_name || m.name || modelId;
        const fullId = modelId.includes('/') ? modelId : `${providerName}/${modelId}`;
        models.push({ id: fullId, label: `${displayName} (${providerName})` });
      });
    });
    return models;
  }, [providers]);

  // Get current value (edited or original)
  const getValue = useCallback((fnName, field) => {
    if (edits[fnName] && edits[fnName][field] !== undefined) {
      return edits[fnName][field];
    }
    const cfg = configs?.[fnName];
    if (!cfg) return field === 'temperature' ? 0.0 : field === 'max_tokens' ? 4096 : '';
    return cfg[field] ?? '';
  }, [edits, configs]);

  const handleEdit = (fnName, field, value) => {
    setEdits(prev => ({
      ...prev,
      [fnName]: { ...(prev[fnName] || {}), [field]: value }
    }));
    setSaveMessage(null);
  };

  const handleTest = async (fnName) => {
    const modelId = getValue(fnName, 'model_id');
    if (!modelId) return;
    setTestResults(prev => ({ ...prev, [fnName]: { status: 'testing' } }));
    try {
      const result = await testModelConfig(modelId);
      setTestResults(prev => ({
        ...prev,
        [fnName]: {
          status: result.success ? 'success' : 'fail',
          latency: result.latency_ms || result.latency,
          error: result.error
        }
      }));
    } catch (err) {
      setTestResults(prev => ({
        ...prev,
        [fnName]: { status: 'fail', error: err.response?.data?.detail || err.message }
      }));
    }
  };

  const handleSaveAll = async () => {
    const changedFunctions = Object.keys(edits);
    if (changedFunctions.length === 0) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      for (const fnName of changedFunctions) {
        const payload = {
          model_id: getValue(fnName, 'model_id'),
          temperature: parseFloat(getValue(fnName, 'temperature')) || 0.0,
          max_tokens: parseInt(getValue(fnName, 'max_tokens')) || 4096,
        };
        await updateFunctionConfig(fnName, payload);
      }
      setEdits({});
      refetchConfigs();
      setSaveMessage({ type: 'success', text: `Saved ${changedFunctions.length} function config(s).` });
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  if (configsLoading || providersLoading) {
    return (
      <div className="mb-6 vault-card p-4 flex items-center gap-2 text-[var(--text-muted)]">
        <ArrowPathIcon className="h-4 w-4 animate-spin" />
        Loading per-function model configs...
      </div>
    );
  }

  const functionNames = configs ? Object.keys(configs) : [];

  if (functionNames.length === 0) {
    return null; // No function configs available
  }

  const hasEdits = Object.keys(edits).length > 0;

  return (
    <div className="mb-6">
      <div className="vault-card p-5">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-primary)] mb-1">
          Per-Function Model Configuration
        </h3>
        <p className="text-xs text-[var(--text-muted)] mb-4">
          Override the global model for specific functions. Each function can use a different model, temperature, and token limit.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--vault-border)]">
                <th className="text-left py-2 pr-3 text-[var(--text-muted)] font-medium">Function</th>
                <th className="text-left py-2 px-3 text-[var(--text-muted)] font-medium">Model</th>
                <th className="text-left py-2 px-3 text-[var(--text-muted)] font-medium w-28">Temp</th>
                <th className="text-left py-2 px-3 text-[var(--text-muted)] font-medium w-28">Max Tokens</th>
                <th className="text-center py-2 pl-3 text-[var(--text-muted)] font-medium w-24">Test</th>
              </tr>
            </thead>
            <tbody>
              {functionNames.map(fnName => {
                const modelId = getValue(fnName, 'model_id');
                const temperature = getValue(fnName, 'temperature');
                const maxTokens = getValue(fnName, 'max_tokens');
                const test = testResults[fnName];
                const isEdited = !!edits[fnName];

                return (
                  <tr key={fnName} className={`border-b border-[var(--vault-border)] ${isEdited ? 'bg-[var(--vault-elevated)]' : ''}`}>
                    <td className="py-3 pr-3">
                      <span className="font-semibold text-[var(--text-primary)]">
                        {fnName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <select
                        value={modelId}
                        onChange={(e) => handleEdit(fnName, 'model_id', e.target.value)}
                        className="block w-full pl-2 pr-8 py-1.5 text-xs border border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] rounded-md focus:ring-[var(--spotlight-gold)] focus:border-[var(--spotlight-gold)]"
                      >
                        <option value="">-- select --</option>
                        {allModels.map(m => (
                          <option key={m.id} value={m.id}>{m.label}</option>
                        ))}
                        {/* Keep current value if not in dropdown */}
                        {modelId && !allModels.find(m => m.id === modelId) && (
                          <option value={modelId}>{modelId}</option>
                        )}
                      </select>
                    </td>
                    <td className="py-3 px-3">
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.1}
                        value={temperature}
                        onChange={(e) => handleEdit(fnName, 'temperature', parseFloat(e.target.value) || 0)}
                        className="block w-full px-2 py-1.5 text-xs border border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] rounded-md focus:ring-[var(--spotlight-gold)] focus:border-[var(--spotlight-gold)]"
                      />
                    </td>
                    <td className="py-3 px-3">
                      <input
                        type="number"
                        min={1}
                        max={128000}
                        step={256}
                        value={maxTokens}
                        onChange={(e) => handleEdit(fnName, 'max_tokens', parseInt(e.target.value) || 4096)}
                        className="block w-full px-2 py-1.5 text-xs border border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] rounded-md focus:ring-[var(--spotlight-gold)] focus:border-[var(--spotlight-gold)]"
                      />
                    </td>
                    <td className="py-3 pl-3 text-center">
                      <button
                        onClick={() => handleTest(fnName)}
                        disabled={!modelId || test?.status === 'testing'}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-[var(--vault-border)] text-[var(--text-secondary)] hover:bg-[var(--vault-elevated)] disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Send a test prompt to this model"
                      >
                        {test?.status === 'testing' ? (
                          <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <BeakerIcon className="h-3.5 w-3.5" />
                        )}
                        Test
                      </button>
                      {test?.status === 'success' && (
                        <div className="mt-1 text-xs text-[var(--success)]">
                          {test.latency != null ? `${Math.round(test.latency)}ms` : 'OK'}
                        </div>
                      )}
                      {test?.status === 'fail' && (
                        <div className="mt-1 text-xs text-[var(--error)]" title={test.error}>
                          Failed
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Save All button */}
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={handleSaveAll}
            disabled={!hasEdits || saving}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-black bg-[var(--spotlight-gold)] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                Save All
              </>
            )}
          </button>
          {hasEdits && (
            <span className="text-xs text-amber-400">Unsaved changes</span>
          )}
          {saveMessage && (
            <span className={`text-xs ${saveMessage.type === 'success' ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
              {saveMessage.text}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const Settings = () => {
  const [activeSection, setActiveSection] = useState('models');
  const [localConfig, setLocalConfig] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [fieldWarnings, setFieldWarnings] = useState({});
  const [reindexStatus, setReindexStatus] = useState(null); // null, 'running', 'done', 'error'
  const [reindexResult, setReindexResult] = useState(null);
  const [foiaReindexStatus, setFoiaReindexStatus] = useState(null);
  const [foiaReindexResult, setFoiaReindexResult] = useState(null);

  const queryClient = useQueryClient();

  // Fetch current configuration
  const { data: configData, isLoading, error } = useQuery({
    queryKey: ['configuration'],
    queryFn: async () => {
      const response = await api.get('/config/');
      return response.data;
    }
  });

  // Fetch configuration schema
  const { data: schemaData } = useQuery({
    queryKey: ['configuration-schema'],
    queryFn: async () => {
      const response = await api.get('/config/schema');
      return response.data;
    }
  });

  // Update configuration mutation
  const updateConfigMutation = useMutation({
    mutationFn: async ({ section, updates }) => {
      const response = await api.put('/config/', { section, updates });
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries(['configuration']);
        setUnsavedChanges(false);
        setValidationErrors({});
        setFieldWarnings({});
      } else {
        setValidationErrors({ [activeSection]: data.errors || ['Update failed'] });
      }
    },
    onError: (error) => {
      setValidationErrors({ 
        [activeSection]: [error.response?.data?.detail || 'Failed to update configuration'] 
      });
    }
  });

  // Reset configuration mutation
  const resetConfigMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/config/reset');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['configuration']);
      setLocalConfig({});
      setUnsavedChanges(false);
      setValidationErrors({});
      setFieldWarnings({});
    }
  });

  // Initialize local config when data loads
  useEffect(() => {
    if (configData?.configuration) {
      setLocalConfig(configData.configuration);
    }
  }, [configData]);

  // Handle input changes
  const handleInputChange = (section, field, value) => {
    let newConfig = {
      ...localConfig,
      [section]: {
        ...localConfig[section],
        [field]: value
      }
    };
    
    // Special handling for LLM provider changes
    if (section === 'models' && field === 'llm_provider') {
      // Set appropriate default model when switching providers
      if (value === 'bedrock' && !newConfig.models.bedrock_model_id) {
        newConfig.models.bedrock_model_id = 'amazon.nova-pro-v1:0';
      } else if (value === 'anthropic' && !newConfig.models.anthropic_model_id) {
        newConfig.models.anthropic_model_id = 'claude-sonnet-4-20250514';
      }
      
      // Clear validation errors when switching providers
      setValidationErrors(prev => ({
        ...prev,
        [section]: []
      }));
      
      // Clear field warnings as well
      setFieldWarnings(prev => {
        const newWarnings = { ...prev };
        Object.keys(newWarnings).forEach(key => {
          if (key.startsWith(`${section}.`)) {
            delete newWarnings[key];
          }
        });
        return newWarnings;
      });
    }
    
    setLocalConfig(newConfig);
    
    // Check for unsaved changes
    const hasChanges = hasUnsavedChanges(configData?.configuration || {}, newConfig);
    setUnsavedChanges(hasChanges);
    
    // Validate the section
    if (schemaData?.schema) {
      const sectionErrors = validateSectionConfig(section, newConfig[section] || {}, schemaData.schema);
      setValidationErrors(prev => ({
        ...prev,
        [section]: sectionErrors
      }));
      
      // Get field warnings
      const warnings = getFieldWarnings(section, field, value);
      setFieldWarnings(prev => ({
        ...prev,
        [`${section}.${field}`]: warnings
      }));
    }
  };

  // Save changes for current section
  const handleSaveSection = () => {
    const sectionConfig = localConfig[activeSection] || {};
    
    // Validate before saving
    if (schemaData?.schema) {
      const errors = validateSectionConfig(activeSection, sectionConfig, schemaData.schema);
      if (errors.length > 0) {
        setValidationErrors(prev => ({
          ...prev,
          [activeSection]: errors
        }));
        return;
      }
    }
    
    const originalConfig = configData?.configuration?.[activeSection] || {};
    const changedFields = getChangedFields(originalConfig, sectionConfig);
    
    if (Object.keys(changedFields).length > 0) {
      const updates = {};
      Object.keys(changedFields).forEach(key => {
        updates[key] = changedFields[key].to;
      });
      
      updateConfigMutation.mutate({ section: activeSection, updates });
    }
  };


  // Render form field based on schema
  const renderField = (section, fieldName, fieldConfig) => {
    let value = localConfig[section]?.[fieldName] ?? fieldConfig.default;
    
    // Ensure we have valid defaults for model fields
    if (section === 'models') {
      if (fieldName === 'bedrock_model_id' && !value) {
        value = 'amazon.nova-pro-v1:0';
      } else if (fieldName === 'anthropic_model_id' && !value) {
        value = 'claude-sonnet-4-20250514';
      }
    }
    const fieldId = `${section}-${fieldName}`;
    const warnings = fieldWarnings[`${section}.${fieldName}`] || [];
    const hasFieldError = validationErrors[section]?.some(error => 
      error.toLowerCase().includes(fieldConfig.label.toLowerCase())
    );

    switch (fieldConfig.type) {
      case 'boolean':
        return (
          <div>
            <div className="flex items-center">
              <input
                id={fieldId}
                type="checkbox"
                checked={value}
                onChange={(e) => handleInputChange(section, fieldName, e.target.checked)}
                className={`h-4 w-4 text-[var(--spotlight-gold)] focus:ring-[var(--spotlight-gold)] border-[var(--vault-border)] rounded ${
                  hasFieldError ? 'border-red-300' : ''
                }`}
              />
              <div className="ml-2 flex items-center">
                <label htmlFor={fieldId} className="block text-sm text-[var(--text-primary)]">
                  {fieldConfig.label}
                </label>
                <SettingTooltip 
                  label="" 
                  tooltip={getSettingTooltip(section, fieldName)}
                  position="right"
                />
              </div>
            </div>
            {warnings.length > 0 && (
              <div className="mt-1">
                {warnings.map((warning, index) => (
                  <div key={index} className="flex items-center text-amber-600 text-xs">
                    <InformationCircleIcon className="h-3 w-3 mr-1" />
                    {warning}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'select':
        return (
          <div>
            <div className="mb-1">
              <SettingTooltip 
                label={fieldConfig.label}
                tooltip={getSettingTooltip(section, fieldName)}
                position="right"
              />
            </div>
            <select
              id={fieldId}
              value={value}
              onChange={(e) => handleInputChange(section, fieldName, e.target.value)}
              className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] focus:outline-none focus:ring-[var(--spotlight-gold)] focus:border-[var(--spotlight-gold)] rounded-md ${
                hasFieldError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
              }`}
            >
              {(fieldConfig.options || []).map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {warnings.length > 0 && (
              <div className="mt-1">
                {warnings.map((warning, index) => (
                  <div key={index} className="flex items-center text-amber-600 text-xs">
                    <InformationCircleIcon className="h-3 w-3 mr-1" />
                    {warning}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'number':
        return (
          <div>
            <div className="mb-1">
              <SettingTooltip 
                label={fieldConfig.label}
                tooltip={getSettingTooltip(section, fieldName)}
                position="right"
              />
            </div>
            <input
              id={fieldId}
              type="number"
              value={value}
              min={fieldConfig.min}
              max={fieldConfig.max}
              step={fieldConfig.step || 1}
              onChange={(e) => {
                let val = parseFloat(e.target.value) || 0;
                if (fieldConfig.min !== undefined && val < fieldConfig.min) val = fieldConfig.min;
                if (fieldConfig.max !== undefined && val > fieldConfig.max) val = fieldConfig.max;
                handleInputChange(section, fieldName, val);
              }}
              className={`mt-1 block w-full border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] rounded-md shadow-sm focus:ring-[var(--spotlight-gold)] focus:border-[var(--spotlight-gold)] ${
                hasFieldError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
              }`}
            />
            {fieldConfig.min !== undefined || fieldConfig.max !== undefined ? (
              <div className="mt-1 text-xs text-[var(--text-muted)]">
                {fieldConfig.min !== undefined && fieldConfig.max !== undefined 
                  ? `Range: ${fieldConfig.min} - ${fieldConfig.max}`
                  : fieldConfig.min !== undefined 
                    ? `Minimum: ${fieldConfig.min}`
                    : `Maximum: ${fieldConfig.max}`
                }
              </div>
            ) : null}
            {warnings.length > 0 && (
              <div className="mt-1">
                {warnings.map((warning, index) => (
                  <div key={index} className="flex items-center text-amber-600 text-xs">
                    <InformationCircleIcon className="h-3 w-3 mr-1" />
                    {warning}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div>
            <div className="mb-1">
              <SettingTooltip 
                label={fieldConfig.label}
                tooltip={getSettingTooltip(section, fieldName)}
                position="right"
              />
            </div>
            <input
              id={fieldId}
              type="text"
              value={value}
              onChange={(e) => handleInputChange(section, fieldName, e.target.value)}
              className="mt-1 block w-full border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] rounded-md shadow-sm focus:ring-[var(--spotlight-gold)] focus:border-[var(--spotlight-gold)]"
            />
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <ArrowPathIcon className="h-5 w-5 animate-spin text-[var(--spotlight-gold)]" />
          <span className="text-[var(--text-primary)]">Loading configuration...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Failed to Load Configuration</h2>
          <p className="text-[var(--text-muted)]">{error.message}</p>
        </div>
      </div>
    );
  }

  const schema = (schemaData?.schema && typeof schemaData.schema === 'object' && !Array.isArray(schemaData.schema)) ? schemaData.schema : {};
  const sections = Object.keys(schema);

  return (
    <div className="min-h-screen">
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[var(--text-primary)] flex items-center">
              <CogIcon className="h-8 w-8 mr-3 text-[var(--spotlight-gold)]" />
              System Configuration
            </h1>
            <p className="mt-2 text-[var(--text-muted)]">
              Configure models, parameters, and system behavior for the DECLASS platform.
            </p>
          </div>

          <div className="bg-[var(--vault-surface)] border border-[var(--vault-border)] rounded-lg overflow-hidden">
            <div className="flex">
              {/* Sidebar */}
              <div className="w-64 bg-[var(--vault-elevated)] border-r border-[var(--vault-border)]">
                <nav className="space-y-1 p-4">
                  {sections.map((section) => (
                    <button
                      key={section}
                      onClick={() => setActiveSection(section)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeSection === section
                          ? 'bg-[var(--vault-surface)] text-[var(--spotlight-gold)]'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--vault-surface)]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{schema[section]?.title || section}</span>
                        {unsavedChanges && activeSection === section && (
                          <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                        )}
                      </div>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Main Content */}
              <div className="flex-1 p-6">
                {schema[activeSection] && (
                  <div>
                    <div className="mb-6">
                      <h2 className="text-xl font-semibold text-[var(--text-primary)]">
                        {schema[activeSection].title}
                      </h2>
                      <p className="mt-1 text-[var(--text-muted)]">
                        {schema[activeSection].description}
                      </p>
                    </div>

                    {/* Validation Errors */}
                    {validationErrors[activeSection] && validationErrors[activeSection].length > 0 && (
                      <div className="mb-6 bg-red-900/20 border border-red-800 rounded-md p-4">
                        <div className="flex">
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-400">
                              Configuration Errors
                            </h3>
                            <div className="mt-2 text-sm text-red-300">
                              <ul className="list-disc list-inside space-y-1">
                                {validationErrors[activeSection].map((error, index) => (
                                  <li key={index}>{error}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Weight Validation Warning */}
                    {validationErrors[activeSection]?.some(error => error.includes('weight')) && (
                      <div className="mb-6 bg-yellow-900/20 border border-yellow-800 rounded-md p-4">
                        <div className="flex">
                          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-400">
                              Weight Configuration Warning
                            </h3>
                            <p className="mt-1 text-sm text-yellow-300">
                              {activeSection === 'search' 
                                ? 'Original Score Weight + Enhancement Weight must equal 1.0'
                                : 'Keyword Match Weight + Semantic Match Weight must equal 1.0'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Model Switcher for Models Section */}
                    {activeSection === 'models' && (
                      <div className="mb-6">
                        <ModelSwitcher showAsCard={true} />
                      </div>
                    )}

                    {/* Per-Function Model Configuration */}
                    {activeSection === 'models' && (
                      <PerFunctionModelConfig />
                    )}

                    {/* Configuration Fields */}
                    <div className="space-y-6">
                      {Object.entries(schema[activeSection].fields || {}).map(([fieldName, fieldConfig]) => {
                        // Conditional rendering for model fields based on provider selection
                        if (activeSection === 'models') {
                          const currentProvider = localConfig.models?.llm_provider || 'bedrock';
                          
                          // Only show Bedrock model field when Bedrock is selected
                          if (fieldName === 'bedrock_model_id' && currentProvider !== 'bedrock') {
                            return null;
                          }
                          
                          // Only show Anthropic model field when Anthropic is selected
                          if (fieldName === 'anthropic_model_id' && currentProvider !== 'anthropic') {
                            return null;
                          }
                        }
                        
                        return (
                          <div key={fieldName} className="border-b border-[var(--vault-border)] pb-4">
                            {renderField(activeSection, fieldName, fieldConfig)}
                            {fieldConfig.description && (
                              <p className="mt-1 text-xs text-[var(--text-muted)]">
                                {fieldConfig.description}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Actions */}
                    <div className="mt-8 flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={handleSaveSection}
                          disabled={!unsavedChanges || updateConfigMutation.isPending || (validationErrors[activeSection]?.length > 0)}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-black bg-[var(--spotlight-gold)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--spotlight-gold)] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {updateConfigMutation.isPending ? (
                            <>
                              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <CheckCircleIcon className="h-4 w-4 mr-2" />
                              Save Changes
                            </>
                          )}
                        </button>

                        {unsavedChanges && (
                          <span className="text-sm text-amber-400 font-medium">
                            You have unsaved changes
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => resetConfigMutation.mutate()}
                        disabled={resetConfigMutation.isPending}
                        className="inline-flex items-center px-4 py-2 border border-[var(--vault-border)] text-sm font-medium rounded-md text-[var(--text-secondary)] bg-[var(--vault-surface)] hover:bg-[var(--vault-elevated)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--spotlight-gold)]"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                        Reset to Defaults
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Embedding Reindex Card */}
          <div className="mt-8 vault-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-primary)] mb-1">
                  Embedding Model &amp; SCG Index
                </h3>
                <p className="text-sm text-[var(--text-muted)]">
                  Re-embed all SCG rules with the active embedding model. Required after switching models (e.g. to BGE-M3).
                </p>
                {reindexResult && (
                  <div className={`mt-3 text-sm font-mono ${reindexResult.success ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                    {reindexResult.message || reindexResult.error}
                  </div>
                )}
              </div>
              <button
                onClick={async () => {
                  setReindexStatus('running');
                  setReindexResult(null);
                  try {
                    const result = await reindexSCGRules();
                    setReindexResult(result);
                    setReindexStatus('done');
                  } catch (err) {
                    setReindexResult({ success: false, error: err.response?.data?.detail || err.message });
                    setReindexStatus('error');
                  }
                }}
                disabled={reindexStatus === 'running'}
                className="vault-btn vault-btn-secondary flex items-center gap-2 whitespace-nowrap"
              >
                <ArrowPathIcon className={`w-4 h-4 ${reindexStatus === 'running' ? 'animate-spin' : ''}`} />
                {reindexStatus === 'running' ? 'Re-indexing...' : 'Re-index SCG Rules'}
              </button>
            </div>
          </div>

          {/* FOIA Rules Reindex Card */}
          <div className="mt-8 vault-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-primary)] mb-1">
                  FOIA Rules Index
                </h3>
                <p className="text-sm text-[var(--text-muted)]">
                  Re-embed all FOIA rules with the active embedding model. Required after switching models to fix dimension mismatches.
                </p>
                {foiaReindexResult && (
                  <div className={`mt-3 text-sm font-mono ${foiaReindexResult.success ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                    {foiaReindexResult.message || foiaReindexResult.error}
                  </div>
                )}
              </div>
              <button
                onClick={async () => {
                  setFoiaReindexStatus('running');
                  setFoiaReindexResult(null);
                  try {
                    const result = await reindexFOIARules();
                    setFoiaReindexResult(result);
                    setFoiaReindexStatus('done');
                  } catch (err) {
                    setFoiaReindexResult({ success: false, error: err.response?.data?.detail || err.message });
                    setFoiaReindexStatus('error');
                  }
                }}
                disabled={foiaReindexStatus === 'running'}
                className="vault-btn vault-btn-secondary flex items-center gap-2 whitespace-nowrap"
              >
                <ArrowPathIcon className={`w-4 h-4 ${foiaReindexStatus === 'running' ? 'animate-spin' : ''}`} />
                {foiaReindexStatus === 'running' ? 'Re-indexing...' : 'Re-index FOIA Rules'}
              </button>
            </div>
          </div>

          {/* Configuration Tips */}
          <div className="mt-8 bg-[var(--vault-elevated)] border border-[var(--vault-border)] rounded-md p-4">
            <h3 className="text-sm font-medium text-[var(--text-primary)] mb-2">Configuration Tips</h3>
            <ul className="text-sm text-[var(--text-muted)] space-y-1">
              <li>• Changes to model configurations may require system restart for full effect</li>
              <li>• Higher temperature values increase randomness in AI responses</li>
              <li>• Larger embedding models provide better accuracy but slower performance</li>
              <li>• Weight values in search and SCG sections must sum to 1.0</li>
              <li>• OCR processing significantly increases PDF processing time</li>
              <li>• Advanced retrieval algorithms improve rule matching accuracy</li>
              <li>• Semantic SCG retrieval uses vector embeddings for better rule matching</li>
              <li>• Higher similarity thresholds are more selective but may miss relevant content</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;